/**
 * O toque de mensagem nova do painel.
 *
 * Toca em toda mensagem que o cliente manda num atendimento do atendente — inclusive na
 * conversa aberta na tela (03/10). A regra anterior, de só avisar o que estava fora da
 * vista, avisava de menos na prática: quem atende fica com várias janelas e nem sempre
 * está olhando a que está aberta. Quem achar demais desliga no menu, e a escolha é de cada
 * um. É só do painel: o cliente e o técnico não recebem toque nenhum (29/09).
 */

/**
 * Quanto tempo o toque fica em silêncio depois de soar.
 *
 * Curto de propósito: a ideia é ouvir cada mensagem. O que esta janela evita é a rajada —
 * três linhas enviadas no mesmo fôlego são um recado só, e tocariam como uma metralhadora.
 */
const SILENCIO_MS = 1_500;

const CHAVE_PREFERENCIA = 'mymaxx:som-nova-mensagem';
const ARQUIVO = '/nova-mensagem.mp3';

let ultimoToque = 0;
let audio: HTMLAudioElement | null = null;

/** O som está ligado? Ligado é o padrão: quem não quer, desliga. */
export function somLigado(): boolean {
  try {
    return localStorage.getItem(CHAVE_PREFERENCIA) !== 'desligado';
  } catch {
    // Navegador com armazenamento bloqueado: o som funciona, só não lembra da escolha.
    return true;
  }
}

export function definirSom(ligado: boolean): void {
  try {
    localStorage.setItem(CHAVE_PREFERENCIA, ligado ? 'ligado' : 'desligado');
  } catch {
    /* sem onde guardar: a escolha vale só nesta sessão */
  }
}

/**
 * Toca o aviso, respeitando a preferência e a janela de silêncio.
 *
 * O elemento de áudio é um só, reaproveitado: criar um por mensagem deixa lixo preso no
 * navegador. A promessa de `play()` é ignorada de propósito — se o navegador recusar
 * (autoplay antes de qualquer clique na aba), não há o que fazer nem o que avisar.
 */
export function tocarAvisoDeMensagem(): void {
  if (!somLigado()) return;
  const agora = Date.now();
  if (agora - ultimoToque < SILENCIO_MS) return;
  ultimoToque = agora;

  try {
    audio ??= new Audio(ARQUIVO);
    audio.currentTime = 0;
    void audio.play().catch(() => undefined);
  } catch {
    /* sem áudio disponível: seguir sem toque é melhor do que quebrar a tela */
  }
}

/**
 * O toque da chamada de voz.
 *
 * Em laço, e não um bipe só: uma ligação precisa insistir até alguém atender ou desistir —
 * é o que distingue "estão te chamando agora" de "chegou mensagem". Até 21/10 usava o som
 * de mensagem nova, que dura menos de um segundo: a chamada chegava como um tique e quem
 * estava de costas para a tela não percebia.
 *
 * Não respeita a preferência de som das mensagens de propósito: quem desligou o aviso de
 * mensagem desligou o chiado do dia a dia, não o telefone.
 */
const ARQUIVO_TOQUE = '/ligacao.mp3';
let toque: HTMLAudioElement | null = null;

export function tocarToque(): void {
  try {
    toque ??= new Audio(ARQUIVO_TOQUE);
    toque.loop = true;
    toque.currentTime = 0;
    void toque.play().catch(() => undefined);
  } catch {
    /* sem áudio: a chamada ainda aparece na tela, só não soa */
  }
}

export function pararToque(): void {
  try {
    toque?.pause();
    if (toque) toque.currentTime = 0;
  } catch {
    /* nada a fazer */
  }
}
